<template>
  <v-app>
    <!-- Loading screen while checking auth -->
    <div v-if="$store.state.loading" class="loading-screen">
      <v-progress-circular
        indeterminate
        size="64"
        color="#002060"
      ></v-progress-circular>
      <p class="loading-text">Loading...</p>
    </div>

    <!-- Main app content -->
    <div v-else>
      <!-- Include the Navbar component -->
      <Navbar />

      <v-main style="background: #f2f2f2;min-height:100vh;">
        <transition name="fade" mode="out-in">
          <router-view />
        </transition>
      </v-main>
    </div>
  </v-app>
</template>

<script>
import Navbar from '@/components/Navbar.vue';

export default {
  name: 'App',
  components: {
    Navbar,
  },
  async created() {
    // Initialize authentication state
    await this.$store.dispatch('initAuth');
  }
};
</script>

<style>
/* CSS Custom Properties for Brand Colors */
:root {
  --brand-primary: #002060;
  --brand-primary-dark: #001547;
  --brand-secondary: #BCA258;
  --brand-white: #ffffff;
  --brand-bg-light: #f2f2f2;
  --brand-bg-medium: #e8e8e8;
  --brand-bg-input: #f5f5f5;
  --brand-bg-input-hover: #f8f9fa;
  --brand-text-primary: #002060;
  --brand-text-secondary: #666666;
  --brand-border: #e5e7eb;
  --brand-success: #4caf50;
  --brand-error: #f44336;
  --brand-warning: #ff9800;
  --brand-info: #2196f3;
}

/* Global CSS Reset and Base Styles */
* {
  box-sizing: border-box;
}

html {
  font-size: 14px; /* Smaller base font size for zoom out effect */
  -webkit-text-size-adjust: 100%;
  -moz-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  text-size-adjust: 100%;
}

body {
  margin: 0;
  padding: 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  line-height: 1.4;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--brand-bg-light);
}

/* Responsive typography - smaller across all breakpoints */
@media (max-width: 1200px) {
  html {
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  html {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  html {
    font-size: 11px;
  }
}

/* Container max-widths for different screen sizes - more compact */
.container-responsive {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

@media (min-width: 1600px) {
  .container-responsive {
    max-width: 1500px;
  }
}

@media (max-width: 768px) {
  .container-responsive {
    padding: 0 0.75rem;
  }
}

/* Consistent card styling */
.card-consistent {
  background: var(--brand-white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--brand-border);
}

/* Consistent spacing system */
.spacing-xs { margin: 0.25rem; }
.spacing-sm { margin: 0.5rem; }
.spacing-md { margin: 0.75rem; }
.spacing-lg { margin: 1rem; }
.spacing-xl { margin: 1.5rem; }

.padding-xs { padding: 0.25rem; }
.padding-sm { padding: 0.5rem; }
.padding-md { padding: 0.75rem; }
.padding-lg { padding: 1rem; }
.padding-xl { padding: 1.5rem; }

/* Consistent button system */
.btn-primary {
  background-color: var(--brand-primary) !important;
  color: var(--brand-white) !important;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.btn-primary:hover {
  background-color: var(--brand-primary-dark) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 32, 96, 0.3);
}

.btn-secondary {
  background-color: transparent !important;
  color: var(--brand-primary) !important;
  border: 1px solid var(--brand-primary);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background-color: var(--brand-primary) !important;
  color: var(--brand-white) !important;
}

/* Loading screen styles */
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: var(--brand-bg-light);
}

.loading-text {
  margin-top: 16px;
  font-size: 1.1rem;
  color: var(--brand-primary);
  font-family: 'Montserrat', sans-serif;
}

/* Attach this class to your v-data-table: <v-data-table class="custom-header" ...> */

/* Make the header row container allow rounded corners */
.custom-header .v-data-table__thead {
  background-color: var(--brand-primary) !important;
  border-collapse: separate;        /* allow cell-level radii */
  overflow: hidden;                 /* clip overflowing corners */
}

/* Base styling for every header cell */
.custom-header .v-data-table__th {
  background-color: var(--brand-primary) !important;
  white-space: nowrap;
  color: var(--brand-white) !important;
  text-transform: none;             /* keep original casing */
}

/* Top-left corner radius on the very first <th> */
.custom-header .v-data-table__th:first-child {
  border-top-left-radius: 20px !important;
}

/* Top-right corner radius on the very last <th> */
.custom-header .v-data-table__th:last-child {
  border-top-right-radius: 20px !important;
}

/* Optional: striped row background */
.custom-header .v-data-table__tr:nth-child(odd) {
  background-color: var(--brand-white) !important;
}

.custom-header .v-data-table__tr:nth-child(even) {
  background-color: var(--brand-bg-light) !important;
}

/* Global responsive utilities */
.text-responsive {
  font-size: clamp(0.875rem, 2.5vw, 1rem);
}

.heading-responsive {
  font-size: clamp(1.25rem, 4vw, 2rem);
}

.padding-responsive {
  padding: clamp(0.5rem, 2vw, 1.5rem);
}

.margin-responsive {
  margin: clamp(0.5rem, 2vw, 1rem);
}

/* Vuetify component overrides for consistent, smaller scaling */
:deep(.v-btn) {
  font-size: 0.75rem !important;
  min-height: 32px !important;
  padding: 0.375rem 0.75rem !important;
}

:deep(.v-btn--size-large) {
  font-size: 0.875rem !important;
  min-height: 40px !important;
  padding: 0.5rem 1rem !important;
}

:deep(.v-data-table) {
  font-size: 0.75rem !important;
}

:deep(.v-data-table .v-data-table__th) {
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  padding: 0.5rem !important;
  text-align: center !important;
}

:deep(.v-data-table .v-data-table__td) {
  font-size: 0.75rem !important;
  padding: 0.5rem !important;
  text-align: center !important;
}

:deep(.v-text-field .v-field__input) {
  font-size: 0.875rem !important;
  min-height: 36px !important;
}

:deep(.v-text-field .v-field-label) {
  font-size: 0.75rem !important;
}

:deep(.v-card-title) {
  font-size: 1rem !important;
  font-weight: 600 !important;
  padding: 1rem !important;
}

:deep(.v-card-text) {
  font-size: 0.875rem !important;
  padding: 0.75rem 1rem !important;
}

:deep(.v-chip) {
  font-size: 0.6875rem !important;
  height: 24px !important;
}

:deep(.v-select .v-field__input) {
  font-size: 0.875rem !important;
}

:deep(.v-textarea .v-field__input) {
  font-size: 0.875rem !important;
}

/* Consistent form styling */
:deep(.v-field--variant-solo) {
  border-radius: 6px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: var(--brand-bg-input-hover) !important;
}

/* Consistent dialog styling */
:deep(.v-dialog .v-card) {
  border-radius: 8px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

/* Router transition effects */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
